import { setAttendeeViewEventMembers } from '@goldcast/api/backend';
import { ConfirmationDialog, GenericMessageDialog } from 'uicomponents';

import { EventStageGetters } from './getters';
import { EventStageMutations } from './mutations';
import { DemoRequestSubmitType, EventStageState, ProgressBarConfig } from './types';

import type { AgendaItem, Booth, Broadcast } from '../event/types';

import analytics from '@/analytics';
import ConfirmationPopup from '@/components/core/ConfirmationPopup.vue';
import { AGORA, HEARTBEAT_INTERVAL_TIME } from '@/constants';
import i18n from '@/i18n';
import DialogService from '@/services/dialogs/DialogService';
import { getEventUserData, getEventUsersById, logBoothCtaClick, updateEventUser } from '@/services/event.service.js';
import { closeJoiningDelayNetworkErrorModal } from '@/services/liveMedia.service';
import loggerService from '@/services/logger.service';
import { UserRole } from '@/services/user.service';
import { RootState } from '@/store';
import { findCurrentTrack, itemInTrack as isItemInTrack } from '@/store/modules/event/getters';
import { BroadcastActions } from '@/store/modules/user/types';
import { getRouter, pushRoute } from '@/utils/routerAccess';
import {
  carryOverWatchLive,
  delay,
  getCorrectedRole,
  getCurrentVideoDuration,
  getLiveOrFirstBroadcast,
  getToastDisplayDelay,
  getTotalVideoDuration,
  isSpeakerButNotInCurrentBroadcast,
  isStageOnItem,
  isTransitionable,
  isWithinVideoBroadcastActivityLogWindow,
  save,
  shouldShowProgressBar,
  showOrganizerRedirectionModal
} from '@/utils/stage';
import { handleLiveRunOfShowAutomation } from '@/views/event/BackstageControls/Toolbar/LiveRunOfShow/utils/commonUtils';
import { handleRunOfShow } from '@/views/event/BackstageControls/Toolbar/RunOfShow/utils';

export type EventStageContext = {
  state: EventStageState;
  rootState: RootState;
  getters: EventStageGetters;
  rootGetters: { [k: string]: any };
  commit: <k extends keyof EventStageMutations>(mutation: k, payload?: any, config?: { root: boolean }) => any;
  dispatch: (action: string, params?: any, settings?: { root: boolean }) => Promise<any>;
};

export default {
  startHeartBeat(context: EventStageContext) {
    context.commit('clearHeartBeatInterval');
    context.commit(
      'setHeartBeatInterval',
      setInterval(() => {
        const serverAdjustedTime = new Date(Date.now() + context.rootState.timeSync.offset);
        context.commit('setHeartBeat', serverAdjustedTime);
        context.dispatch('calculateTimeDerivativeStageProps');
      }, HEARTBEAT_INTERVAL_TIME)
    );
  },
  calculateTimeDerivativeStageProps(context: EventStageContext) {
    const currentBroadcast: Broadcast = context.rootGetters['event/currentEventBroadcast'](
      context.rootState.route.params.id
    );
    const currentUser = context.rootGetters.getCurrentUser?.id;
    const isTVMode = context.rootState.event.stage?.isTVMode;

    const isSpeaker = (currentBroadcast?.speakerIds || []).includes(currentUser) && !isTVMode;
    const isOrganizer = context.rootGetters['event/isBroadcastOrganizer'] && !isTVMode;
    const isAuthorizedForBackstage = isSpeaker || isOrganizer;
    const stageDetails = {
      currentBroadcast,
      isAuthorizedForBackstage
    };
    context.dispatch('calculateProgressBarConfig', stageDetails, {
      root: false
    });
    context.commit('setIsSimuliveTestBufferMessageShown', stageDetails);
    context.dispatch('updatedActivtiyOfVideoSession', currentBroadcast);

    if (currentBroadcast?.is_live) {
      const canAccessCues = context.rootGetters.isBroadcastActionPermitted(
        BroadcastActions.MODIFY_LIVE_RUN_OF_SHOW,
        currentBroadcast?.id
      );
      if (canAccessCues) {
        const liveRunOfShow = context.rootState.event.liveRunOfShow;
        if (liveRunOfShow && !liveRunOfShow.lastFetch && !liveRunOfShow.loading) {
          context
            .dispatch('event/fetchLiveRunOfShowCues', null, { root: true })
            .catch(err => loggerService.error('Failed to fetch live run of show cues for scheduling', err));
        }
      }
      handleLiveRunOfShowAutomation();
    }
  },
  calculateProgressBarConfig(
    context: EventStageContext,
    { currentBroadcast, isAuthorizedForBackstage }: { currentBroadcast: Broadcast; isAuthorizedForBackstage: boolean }
  ) {
    const defaultProgressBarConfig: ProgressBarConfig = {
      totalVideoDuration: 0,
      currentVideoDuration: 0,
      showProgressBar: false
    };

    const hasValidRunOfShow =
      currentBroadcast?.broadcast_type === 'SIMULIVE' &&
      currentBroadcast?.is_automated_run_of_show &&
      currentBroadcast?.run_of_show_config?.length > 0;
    if (hasValidRunOfShow || (context.state.isHLSVideoPlayerHovered && isAuthorizedForBackstage)) {
      defaultProgressBarConfig.totalVideoDuration = getTotalVideoDuration(context, currentBroadcast);
      defaultProgressBarConfig.currentVideoDuration = getCurrentVideoDuration(
        context,
        currentBroadcast,
        defaultProgressBarConfig.totalVideoDuration
      );

      if (hasValidRunOfShow && currentBroadcast?.is_live) {
        handleRunOfShow(defaultProgressBarConfig.currentVideoDuration / 1000);
      }
      defaultProgressBarConfig.showProgressBar =
        context.state.isHLSVideoPlayerHovered &&
        isAuthorizedForBackstage &&
        shouldShowProgressBar(
          context,
          currentBroadcast,
          defaultProgressBarConfig.totalVideoDuration,
          defaultProgressBarConfig.currentVideoDuration
        );
    }

    context.state.progressBarConfig = defaultProgressBarConfig;
  },
  updatedActivtiyOfVideoSession(context: EventStageContext, currentBroadcast: Broadcast) {
    if (!context.rootState.route.query?.record_mode) {
      return;
    }

    if (currentBroadcast?.broadcast_type !== 'SIMULIVE' && currentBroadcast?.broadcast_type !== 'LIVE_EXTERNAL') {
      return;
    }

    const currentBroadcastStartTime = currentBroadcast?.start_time ? new Date(currentBroadcast.start_time) : new Date();
    const currentBroadcastEndTime = currentBroadcast?.end_time ? new Date(currentBroadcast.end_time) : new Date();

    const currentTime = context.state?.heartBeatDate || new Date();

    const isWithinBroadcastStartLogRange = isWithinVideoBroadcastActivityLogWindow(
      currentBroadcastStartTime,
      currentTime
    );
    const isWithinBroadcastEndLogRange = isWithinVideoBroadcastActivityLogWindow(currentBroadcastEndTime, currentTime);

    if (!isWithinBroadcastStartLogRange && !isWithinBroadcastEndLogRange) {
      return;
    }

    context.dispatch('mediaroom/updateActivityInRoom', currentBroadcast, {
      root: true
    });
  },
  handleSubMenuClick(context: EventStageContext, { name, params }: { name: string; params?: any }) {
    const routeName = name === 'stage' ? '' : name;
    context.dispatch('gotoPage', { routerName: routeName, routeParams: params });
    if (name.includes('embedded')) {
      const widget = context.rootState.event.widgets?.find(widget => widget.id === name.split('/')[1]);

      if (widget?.configuration?.noMiniPlayer) {
        (context.commit as any)('setMuted', true);
        closeJoiningDelayNetworkErrorModal();
      }
    }
  },
  gotoPage(context: EventStageContext, { routerName, routeParams }: { routerName: string; routeParams?: any }) {
    const { meta, query, params, fullPath } = context.rootState.route;
    const finalParams = { ...params, ...routeParams };
    const cementedQueryVariables = ['tv_mode'];
    const q = { ...query };
    Object.keys(q).forEach(key => {
      if (!cementedQueryVariables.includes(key)) {
        delete q[key];
      }
    });
    if (!meta.prefix) {
      q.next = routerName;
      pushRoute({ name: 'EventLanding', params: finalParams, query: q });
      return;
    }

    let routeName = `${meta.prefix}-${routerName}`;
    if (routerName === 'stageRoll') {
      routeName = routerName;
    }
    if (routerName === 'stageRoll' && fullPath.includes('broadcast')) {
      routeName = 'Broadcast';
    }

    pushRoute({ name: routeName, params: finalParams, query: q }).catch(err => {
      if (err.name !== 'NavigationDuplicated') {
        throw err;
      }
    });
  },
  handleFiltersChange(context: EventStageContext, { name, payload, page }: any) {
    page = page ? page : context.rootGetters['filterBar/currentPageName'];

    if (name === 'clear') {
      return context.dispatch('filterBar/clearFilters', { page }, { root: true });
    }
    if (name === 'clickFilter') {
      context.dispatch('filterBar/clearFilters', { page }, { root: true });
      context.dispatch(
        'filterBar/toggleFilters',
        {
          page
        },
        { root: true }
      );
    } else {
      return context.dispatch(
        'filterBar/updateFilters',
        {
          page,
          key: name,
          value: payload
        },
        { root: true }
      );
    }
    analytics.track('filterquery', {
      page,
      key: name,
      value: payload
    });
  },

  updateAgendaLivenessState(context: EventStageContext, item: AgendaItem) {
    const { timeLeftTillStart } = getToastDisplayDelay(item);

    const isRecording = context.rootGetters['event/isRecording'];
    const hasAdminAccess =
      context.rootGetters['event/isBroadcastSpeakerOrOrganizer'] && !context.rootState.event.stage?.isTVMode;
    setTimeout(() => {
      context.dispatch('toggleAgendaItemIsLive', item);
      // if (context.rootGetters['simuliveroom/shouldUseInhouseSimulive']) {
      //   if (item.is_live) {
      //     context.dispatch('simuliveroom/playVideoForSimuliveBroadcast', item.id, { root: true });
      //   } else {
      //     context.dispatch('simuliveroom/destroySimuliveBroadcastPlaybackContext', null, { root: true });
      //   }
      // }
      if (hasAdminAccess && item.is_live && !isRecording) {
        context.dispatch('obtainPermissionAndRedirect', item);
      }
    }, timeLeftTillStart);

    loggerService.warn('Not processing transition because page is not transitionable');
  },

  hasPermissionToSeeToast(context: EventStageContext) {
    if (!context.rootState.user.currentEventUser?.eventUser) {
      loggerService.warn(
        "Could not determine user's permissions because currentUser is not present. Retrying in 2 sec!"
      );
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(context.dispatch('hasPermissionToSeeToast'));
        }, 2000);
      });
    }
    loggerService.info('Checking for toast permissions');
    const broadcast = context.rootGetters['event/currentBroadcast'];
    const isSpeakerButNotInBroadcast = isSpeakerButNotInCurrentBroadcast(
      broadcast,
      context.rootState.user.currentEventUser
    );

    if (context.rootGetters['viewAsAttendee'] || isSpeakerButNotInBroadcast) {
      loggerService.info('Priviledged user in a mode where toasts are allowed');
      return true;
    }
    let event_role = context.rootState.user.currentEventUser?.eventUser?.event_role;
    if (event_role === undefined) {
      loggerService.warn('Event role not found for the user! Setting to ATTENDEE_ROLE');
      event_role = UserRole.EVENT_MEMBER;
    }
    return event_role === UserRole.EVENT_MEMBER || event_role === UserRole.SPONSOR;
  },
  showToast(context: EventStageContext, payload: { item: AgendaItem; toastDuration: number }) {
    return context
      .dispatch('hasPermissionToSeeToast')
      .then(allowed => {
        if (allowed) {
          loggerService.info('Setting toast', payload.item);
          context.commit('setTransition', {
            active: true,
            item: payload.item,
            expiresAt: new Date(Date.now() + payload.toastDuration)
          });
        } else {
          loggerService.info('Will not set transition because user is not attendee');
        }
        loggerService.info(
          'Will wait for the toastDuration before resolving the promise to process things that happen after toast'
        );
        return delay(payload.toastDuration, {});
      })
      .catch(err => {
        loggerService.error('Failed when trying to determine permission for showing toast!', err);
      });
  },
  preRedirectCheck(context: EventStageContext, item: AgendaItem) {
    if (!context.rootState.user.currentEventUser?.eventUser) {
      loggerService.warn(
        "Could not determine user's permissions because currentUser is not present. Retrying in 2 sec!"
      );
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(context.dispatch('preRedirectCheck', item));
        }, 2000);
      });
    }
    const event_role = getCorrectedRole(context);

    const currentTrack = findCurrentTrack(
      context.rootState.event.event.tracks,
      context.rootState.user.currentEventUser?.track
    );

    loggerService.info("User's track: ", currentTrack);

    const itemIsInTrack = isItemInTrack(currentTrack, item);

    const isWebinar = context.rootGetters['event/isWebinar'];
    // Only bypass track check for webinars when track has no agenda_items configured (empty track).
    const trackHasNoAgendaItems = !currentTrack?.agenda_items?.length;
    const effectiveItemIsInTrack = (isWebinar && trackHasNoAgendaItems) || itemIsInTrack;

    loggerService.info('Item is in current track: ', currentTrack, itemIsInTrack, {
      isWebinar,
      effectiveItemIsInTrack
    });

    const broadcast = context.rootGetters['event/currentBroadcast'];
    const isSpeakerButNotInBroadcast = isSpeakerButNotInCurrentBroadcast(
      broadcast,
      context.rootState.user.currentEventUser
    );

    if (event_role == UserRole.STAFF) {
      loggerService.info('For staff if item is in track show modal');

      if (effectiveItemIsInTrack && context.rootState.route.params.id !== item.id) {
        // For STAFF show the redirection modal asking if they want to get redirected
        return showOrganizerRedirectionModal(item, context.state.isLiveStreamMode);
      }
      return false; // Do not redirect
    }
    if (event_role === UserRole.EVENT_MEMBER || event_role === UserRole.SPONSOR || isSpeakerButNotInBroadcast) {
      // For attendees and sponsors no modal is needed and redirections are allowed as long as item is in the track
      loggerService.info(
        'For attendee, sponsor and speaker not speaking, redirect if item is in track and auto redirect is enabled or in tv_mode',
        {
          effectiveItemIsInTrack,
          isAutoRedirectEnabled: context.rootState.event.event.is_auto_redirections_enabled,
          tv_mode: context.rootState.route.query.tv_mode
        }
      );
      return (
        effectiveItemIsInTrack &&
        (context.rootState.event.event.is_auto_redirections_enabled || context.rootState.route.query.tv_mode)
      );
    }
    loggerService.info('Not redirecting as no match was found');
    return false; // Do not redirect
  },
  obtainPermissionAndRedirect(context: EventStageContext, item: AgendaItem) {
    return context
      .dispatch('preRedirectCheck', item)
      .then(accepted => {
        loggerService.info("About to redirect checking user's approval for organizers", { accepted });
        if (accepted) {
          context.commit('setTransitionToMainStage', context.state.isLiveStreamMode);
          context.dispatch('applyTransitionOnCurrentTrack', item);
        } else {
          loggerService.info('Redirection failed because modal was cancelled');
        }
      })
      .catch(err => {
        loggerService.error('Redirection failed because modal permission failed! ', err);
      });
  },
  showViewAsAttendeeTooltip(context: EventStageContext) {
    const user = context.rootState.user.currentEventUser;
    const isStage = context.rootState.route.fullPath?.includes('/stage');
    if (!user) {
      loggerService.info('When showing tooltip currentUser was not found! Retrying in 5 sec.');
      setTimeout(() => {
        context.dispatch('showViewAsAttendeeTooltip');
      }, 5000);
    } else if (isStage && context.rootGetters['viewAsAttendee']) {
      context.commit('showViewAsAttendeeTooltip');
    } else if (isStage && !context.rootGetters['viewAsAttendee']) {
      if (localStorage.getItem('was_viewing_as_attendee') === 'true') {
        context.state.attendeeTooltipMessage = 'Welcome back to organizer view';
        context.dispatch('removeAttendeeFlagInLocalStorage', null, {
          root: true
        });
        context.commit('showViewAsAttendeeTooltip');
      } else {
        context.state.attendeeTooltipMessage = '';
      }
    } else {
      loggerService.info('Not showing attendee tooltip when not on stage');
    }
  },
  getPermissionToUpdateModeTo(context: EventStageContext, viewAsAttendee: Boolean) {
    const isSpeaker = context.rootGetters['event/isSpeaker'];
    const text =
      '@@translate@@ Note: You are also a speaker for this event. You will always be able to enter the Backstage area and go on stage.';
    const speakerText = isSpeaker ? i18n.global.t(text) : '';
    const componentprops: any = {
      placement: 'bottom-left',
      cancelOnClickOutside: true,
      on: {
        onConfirm() {
          return Promise.resolve(true);
        },
        onDismiss() {
          return Promise.resolve(false);
        },
        onToggleHideFurtherPrompts() {
          loggerService.error('Hide further dialogs not implemented!');
        }
      }
    };

    const onText =
      '@@translate@@ Switching will reload your current page to the attendee view. To switch back to organizer view toggle off attendee view in the profile dropdown.';
    const offText =
      '@@translate@@ Switching off attendee view will reload your current page and return you to the organizer view. Would you like to continue?';

    const res = DialogService.show(
      ConfirmationDialog,
      {
        header: viewAsAttendee
          ? i18n.global.t('@@translate@@ Switch to Attendee View')
          : i18n.global.t('@@translate@@ Return to Organizer View'),
        bodyText: viewAsAttendee ? i18n.global.t(onText) : i18n.global.t(offText),
        noteText: viewAsAttendee && i18n.global.t(speakerText),
        confirmationBtnText: i18n.global.t('@@translate@@ Switch'),
        confirmationBtnVariant: 'secondary',
        cancelBtnText: i18n.global.t('@@translate@@ Cancel'),
        showDontAskAgain: false,
        noFixedPositioning: true
      },
      componentprops
    );
    return res.promise;
  },
  updateUserMode(context: EventStageContext, viewAsAttendee: Boolean) {
    const user = context.rootState.user.currentUser;
    const isAStaffMember = context.rootGetters['isStaffMember'] || false;
    if (user) {
      const permission = context.dispatch('getPermissionToUpdateModeTo', viewAsAttendee);
      return permission
        .then(accept => {
          if (accept) {
            if (viewAsAttendee) {
              analytics.track('Hubspot_view_as_attendee');
            }

            let promise;
            const eventUserId = Number(context.rootState.user.currentEventUser?.eventUser?.id);
            if (isAStaffMember && eventUserId) {
              promise = setAttendeeViewEventMembers({
                id: eventUserId,
                body: {
                  view_as_attendee: viewAsAttendee as boolean
                }
              });
            } else {
              promise = updateEventUser(
                context.rootState.event.event.id,
                user.id,
                {
                  id: user.id,
                  event: context.rootState.event.event.id,
                  view_as_attendee: viewAsAttendee
                },
                context.rootState.user.currentEventUser?.eventUser?.id
              );
            }
            promise.then(() => {
              const location: any = {
                ...context.rootState.route,
                name: context.rootState.route.name || undefined,
                query: context.rootState.route.query
              };
              getRouter().resolve(location);
              window.open(context.rootState.route.fullPath, '_self');
            });
          }
          loggerService.info('User cancelled attendee popup');
          return Promise.resolve();
        })
        .catch(err => {
          loggerService.error('Error in opening attendee view: ', err);
        });
    }
    loggerService.error('Failed to switch to attendee view as user not found');
  },
  async getLocationForItem(context: EventStageContext, item: AgendaItem) {
    const now = context.rootState.event.stage?.heartBeatDate || new Date();
    const event = context.rootState.event.event;
    let eventEndTime = new Date(event?.end_time);
    if (event?.event_type === 'Webinar' && event?.manual_redirection_enabled) {
      const broadcast = event?.agenda.broadcasts[0];
      eventEndTime = new Date(broadcast?.allowed_end_time || broadcast?.expected_end_time || broadcast?.live_end_time);
    }

    if (!item) {
      throw new Error('Item is required to get location for redirection');
    }
    let action: any;

    if (item.type === 'DISCUSSION_GROUP') {
      action = `/e/${context.rootState.route.params.eventID}/stage/discussiongroups`;
    } else if (item.type === 'BOOTH2') {
      action = `/e/${context.rootState.route.params.eventID}/stage/eventbooths2`;
    } else if (item.type === 'BOOTH') {
      action = `/e/${context.rootState.route.params.eventID}/stage/eventbooths`;
    } else if (
      item.type !== 'BROADCAST' ||
      (item.type === 'BROADCAST' &&
        now >= eventEndTime &&
        !item.is_available_on_demand &&
        context.rootState.event.event.event_type !== 'Recording')
    ) {
      action = { name: 'stageRoll' };
    } else {
      const query = context.state.isLiveStreamMode ? { live_stream: 'true' } : {};
      action = {
        name: 'Broadcast',
        params: {
          id: item?.id
        },
        query
      };
      // For recording, we need to show the recording upload status page
      if (context.rootState.event.event?.is_local_recording) {
        action = {
          name: 'RecordingUploadStatus',
          params: {
            id: item?.id
          },
          query: {
            ...query,
            open_stage: 'true'
          }
        };
      }
    }

    if (context.rootState.event.stage?.isTVMode) {
      if (item.type === 'BROADCAST') {
        return {
          name: 'Broadcast',
          params: {
            id: item?.id
          },
          query: {
            tv_mode: 'true'
          }
        };
      }
      // Return to stage if redirection is non "Broadcast" item
      return {
        name: 'stageRoll',
        query: {
          tv_mode: 'true'
        }
      };
    }

    return action;
  },
  // This will only apply transition if the item is in the track
  applyTransitionOnCurrentTrack(context: EventStageContext, item: AgendaItem) {
    // const userTrack = context.rootGetters["event/userTrack"];
    // const itemInTrack = userTrack.agenda_items.includes(item.id.toString());
    const currentTrack = findCurrentTrack(
      context.rootState.event.event.tracks,
      context.rootState.user.currentEventUser?.track
    );

    loggerService.info("User's track: ", currentTrack);

    const itemInTrack = isItemInTrack(currentTrack, item);

    if (!itemInTrack) {
      loggerService.warn('Transition to item not applied because its not in current track.', item);
      return Promise.reject();
    }
    return context
      .dispatch('getLocationForItem', item)
      .then((action: any) => {
        loggerService.info('Obtained location for redirection: ', action);
        return new Promise((resolve, reject) => {
          carryOverWatchLive(context, item);
          getRouter()
            .push(action)
            .then(() => {
              loggerService.info('Redirection completed!');
              resolve({});
            })
            .catch(err => {
              loggerService.warn('Redirection failed!');
              reject(err);
            });
        });
      })
      .catch(err => {
        loggerService.error('Could not get location for item to redirect', item, err);
      });
  },
  // This will change track if a transition is applied and the item is
  // not in track
  applyTransition(context: EventStageContext, item: AgendaItem) {
    return context
      .dispatch('getLocationForItem', item)
      .then(action => {
        let newTrackRequired: any = null;

        if (item.id && !context.rootGetters['event/isAgendaItemInUserTrack'](item.id)) {
          const tracks = context.rootGetters['event/tracks'];
          const filterTrack = context.rootGetters['filterBar/EventAgendaPage'].trackFilter;

          if (filterTrack.agenda_items?.includes(item.id)) {
            newTrackRequired = filterTrack;
            loggerService.info('newTrackRequired if: ', newTrackRequired, filterTrack);
          } else {
            newTrackRequired = tracks.find((e: any) => e.id && e.agenda_items?.includes(item.id));
            loggerService.info('newTrackRequired else: ', newTrackRequired);
          }
        } else {
          loggerService.info(
            'newTrackRequired will be null because',
            item,
            context.rootGetters['event/isAgendaItemInUserTrack']
          );
        }

        return new Promise((resolve, reject) => {
          if (newTrackRequired) {
            loggerService.info('Switching to a new track');
            context
              .dispatch('event/updateTrack', newTrackRequired, { root: true })
              .then(() => {
                carryOverWatchLive(context, item);
                getRouter()
                  .push(action)
                  .then(() => {
                    loggerService.info('Redirection completed: ', action);
                    resolve({});
                  })
                  .catch((err: any) => {
                    loggerService.warn('Redirection failed: ', action);
                    reject(err);
                  });
              })
              .catch(err => {
                loggerService.info('Switching to new track failed while applying transition: ', newTrackRequired, err);
                reject();
              });
          } else {
            carryOverWatchLive(context, item);
            getRouter()
              .push(action)
              .then(() => {
                loggerService.info('Redirection completed: ', action);
                resolve({});
              })
              .catch(err => {
                loggerService.warn('Redirection failed: ', action);
                reject(err);
              });
          }
        });
      })
      .catch(err => {
        loggerService.error('Could not get location for item to redirect', item, err);
      });
  },
  toggleAgendaItemIsLive(context: EventStageContext, { id, type, is_live }: AgendaItem) {
    const item = context.rootGetters['event/agendaItem'](id, type);
    if (item) {
      (context.commit as any)('event/updateAgendaItem', { ...item, is_live: is_live }, { root: true });
      context.dispatch('liveVideoRoom/handleAgendaVideoPlaybackState', null, {
        root: true
      });
    } else {
      loggerService.info('Toggling agenda item live state failed because it was not found', id, type);
    }
  },

  applyTransitionFromStageTitle(context: EventStageContext) {
    const { event, roomsItem, breakItem, broadcast } = context.getters.currentStage as any;

    if (event || breakItem) {
      context.dispatch('gotoPage', { routerName: 'EventAgendaPage' });
    } else {
      // TODO event and break item is unnecessary here
      context.dispatch('applyTransition', broadcast || roomsItem || breakItem || event);
    }
  },
  transitionOnSocketConnect(context: EventStageContext) {
    const currentAgendaItem: AgendaItem = context.getters.currentRedirectableAgendaItem as any;
    if (!currentAgendaItem) {
      loggerService.info('Could not find current agenda item');
      return;
    }
    const currentRoute = context.rootState.route;
    if (context.rootGetters['event/isBroadcastSpeakerOrOrganizer']) {
      loggerService.info("Won't transition because user is a speaker or organizer");
      return false;
    }
    if (!isTransitionable(currentAgendaItem)) {
      loggerService.info("Won't transition because item is not transitionable");
      return;
    }
    if (isStageOnItem(currentAgendaItem, context.rootState.route, context.rootState)) {
      loggerService.info("Won't transition because stage is on item");
      return;
    }
    let currentItem: AgendaItem = context.rootGetters['event/currentEventBroadcast'](currentRoute.params?.id);

    if (context.rootState.route.fullPath.includes('discussion-room') && !currentItem) {
      const currentDiscussionRoom = context.rootGetters['event/getDiscussionGroup'](currentRoute.params?.id);

      if (currentDiscussionRoom?.is_booth) {
        currentItem = context.rootGetters['event/userTrackAgenda'].find(item => item.type == 'BOOTH' && !item.is_live);
      } else if (!currentDiscussionRoom?.owner && !currentDiscussionRoom?.is_booth) {
        currentItem = context.rootGetters['event/userTrackAgenda'].find(
          item => item.type == 'DISCUSSION_GROUP' && !item.is_live
        );
      }
    }
    if (!currentItem) {
      loggerService.log('session redirection: no current item found to pull out from');
      return;
    }
    context.dispatch(
      'event/handleTransition',
      {
        item: currentItem,
        currentRouteId: currentRoute.params?.id,
        itemBecomingLive: true,
        itemBecomingUnlive: !currentItem?.is_live
      },
      { root: true }
    );
  },
  onDemoLinkClicked(context: EventStageContext) {
    const event = context.rootState.event.event;
    const submitType = event.demo_request_submit_type || DemoRequestSubmitType.ONE_CLICK;

    if (submitType === DemoRequestSubmitType.TWO_STEP) {
      analytics.track('seen_demo_request_consent_dialog', {
        eventId: context.rootState.route?.params?.eventID,
        isAttendee: !context.rootGetters['event/isOrganizerOrSpeaker'],
        broadcastId: context.rootState.route?.params?.id
      });
      DialogService.show(
        ConfirmationPopup,
        {
          title: event.demo_request_consent_title ?? '@@translate@@ Confirm request',
          message: event.demo_request_disclosure_text ?? '',
          confirmText: 'Confirm',
          cancelText: 'Cancel'
        },
        {
          on: {
            onConfirm: () => {
              save(context).then(() => {
                const { index } = DialogService.show(
                  GenericMessageDialog,
                  {
                    title: event.demo_request_title,
                    message: event.demo_request_message,
                    size: 'small',
                    secondaryActionTitle: i18n.global.t('@@translate@@ Okay'),
                    showOverlay: false
                  },
                  {
                    on: {
                      onSecondaryAction: () => {
                        DialogService.close(index);
                      }
                    },
                    cancelOnClickOutside: true
                  }
                );
                if (event.demo_link) {
                  window.open(event.demo_link, '_blank');
                }

                analytics.track('demo_cta_clicked', {
                  eventId: context.rootState.route?.params?.eventID,
                  isAttendee: !context.rootGetters['event/isOrganizerOrSpeaker'],
                  broadcastId: context.rootState.route?.params?.id
                });
              });
            },
            onCancel: () => {
              analytics.track('canceled_demo_request_consent_dialog', {
                eventId: context.rootState.route?.params?.eventID,
                isAttendee: !context.rootGetters['event/isOrganizerOrSpeaker'],
                broadcastId: context.rootState.route?.params?.id
              });
            }
          },
          cancelOnClickOutside: true
        }
      );
    } else {
      return save(context).then(() => {
        DialogService.show(GenericMessageDialog, {
          title: event.demo_request_title,
          message: event.demo_request_message
        });
        if (event.demo_link) {
          window.open(event.demo_link, '_blank');
        }

        analytics.track('demo_cta_clicked', {
          eventId: context.rootState.route?.params?.eventID,
          isAttendee: !context.rootGetters['event/isOrganizerOrSpeaker'],
          broadcastId: context.rootState.route?.params?.id
        });
      });
    }
  },

  openDemoLinkClicked(context: EventStageContext) {
    return save(context).then(() => {
      const url = context.rootState.event.event?.theme_obj?.custom_demo_cta_button_url?.trim() || '';
      const customDemoCtaLinkTo = context.rootState.event.event?.theme_obj?.custom_demo_cta_link_to || '';
      if (url && customDemoCtaLinkTo === 'linkToNewTab') {
        analytics.track('demo_cta_clicked', {
          eventId: context.rootState.route?.params?.eventID,
          isAttendee: !context.rootGetters['event/isOrganizerOrSpeaker'],
          broadcastId: context.rootState.route?.params?.id
        });
        window.open(url, '_blank');
      }
    });
  },
  boothCtaClicked(context: EventStageContext, booth: Booth) {
    return logBoothCtaClick({
      booth: booth.id,
      event_user: context.rootState.user.currentEventUser?.eventUser?.id
    })
      .then(() => {
        analytics.track('Booth CTA Clicked', {
          booth: booth.id
        });
        DialogService.show(GenericMessageDialog, {
          title: booth.demo_request_title || i18n.global.t('@@translate@@ Request Sent'),
          message:
            booth.demo_request_message || i18n.global.t('@@translate@@ Your request for a demo has been submitted')
        });
        return;
      })
      .catch(() => {
        DialogService.show(GenericMessageDialog, {
          title: i18n.global.t('@@translate@@ FAILED!'),
          message: i18n.global.t('@@translate@@ something went wrong while recording the response please try again')
        });
        return new Error('Soemthing went wrong!!');
      });
  },
  async updateBoothCtaData(context: EventStageContext) {
    try {
      if (!context.rootState.user.currentEventUser) {
        const response = await getEventUsersById(context.rootState.route.params?.eventID.toString(), [
          context.rootGetters.getCurrentUserId
        ]);
        const eventUser = response?.data[0];
        context.commit(
          'updateCurrentEventUser' as any,
          {
            eventUser
          },
          { root: true }
        );
      }
      const response = await getEventUserData(context.rootState.user.currentEventUser?.eventUser?.id);
      context.commit(
        'updateCurrentEventUser' as any,
        {
          eventUser: {
            ...context.rootState.user.currentEventUser?.eventUser,
            booth_activities: response?.data?.booth_activities
          }
        },
        { root: true }
      );
    } catch (error) {
      // converting this to warn as this is very well handled in ui. we show a proper ui that action failed
      loggerService.warn('error while fetching user data or booth cta data', error);
    }
  },
  captionActivationHandler(context: EventStageContext, channel: string) {
    const hasUserEnabledCaptions = JSON.parse(window.localStorage.getItem(`enableCaptions-${channel}`) || 'false');
    context.commit('setEnableCaptions', { hasUserEnabledCaptions, channel });
    loggerService.info('Has user enabled captions', hasUserEnabledCaptions);
    if (hasUserEnabledCaptions) {
      const broadcast = context.rootGetters['event/currentEventBroadcast'](context.rootState.route.params.id);
      if (broadcast) {
        context.dispatch('event/avroom/joinCaptionChannel', broadcast?.live_captions_mmid, { root: true });
      } else {
        loggerService.info("Couldn't find broadcast while trying to set caption channel! will retry in 2sec");
        setTimeout(() => {
          const broadcast = context.rootGetters['event/currentEventBroadcast'](context.rootState.route.params.id);
          if (!broadcast) {
            loggerService.info("Couldn't find broadcast on retry! Bailing");
            return;
          }
          context.dispatch('event/avroom/joinCaptionChannel', broadcast?.live_captions_mmid, { root: true });
        }, 2000);
      }
    }
  },
  userDropDownToggle(context: EventStageContext, isDropdownOpen: boolean) {
    context.commit('setUserProfileDropdownOpen', isDropdownOpen);

    const isOrganizer = context.rootGetters['isOrganizer'];

    if (isOrganizer) {
      // Start/Restart the polling whenever the dropdown is opened
      context.dispatch('event/avroom/toggleOnlineSocketCountPolling', isDropdownOpen, { root: true });
    } else {
      loggerService.info('Dropdown toggled. No action required.');
    }
  },
  isCurrentItemTransitionable(context: EventStageContext) {
    return isStageOnItem(context.getters.currentAgendaItem as any, context.rootState.route, context.rootState);
  },
  toggleSetupMode(context: EventStageContext) {
    if (!context.state.isLiveStreamMode) {
      let broadcast = context.rootGetters['event/currentBroadcast'];

      if (!broadcast.id) {
        broadcast =
          getLiveOrFirstBroadcast(context.rootGetters['event/userTrackAgenda']) ||
          getLiveOrFirstBroadcast(context.rootGetters['event/currentEventAgenda']);
      }

      if (broadcast) {
        const { href } = getRouter().resolve({
          name: 'Broadcast',
          params: {
            id: broadcast?.id
          },
          query: {
            live_stream: 'true'
          }
        });
        window.location.href = href;
      }
    } else {
      // const route: Route = route;
      context.commit('setTvWindow', undefined);
      window.location.href = getRouter().resolve({
        ...context.rootState.route,
        query: { ...context.rootState.route.query, live_stream: undefined }
      } as any).href;
    }
  },
  toggleGreenRoomVideoMute(context: EventStageContext) {
    const isMuted = context.state.greenRoomVideoMuted;
    const broadcast = context.rootGetters['event/currentBroadcast'];

    context.dispatch(
      'mediaroom/setRemoteVideoSubscription',
      { channelName: broadcast.id + AGORA.UID_SUFFIX.BACKSTAGE, value: isMuted },
      { root: true }
    );

    context.commit('toggleGreenRoomVideoMuted');
  }
};
